import { createContext, useContext } from "react";
import type {
  Booking,
  CustomerPreOrder,
  CustomerPreOrderItemUpdateRequest,
  PreorderMenu,
} from "../../../common/jsonRpcTypes";

export interface LinkedBooking {
  bookingTypeId: string;
  bookingTypeName: string;
  menus: PreorderMenu[];
}

export interface BookingTypeBasket {
  bookingType: string;
  customerPreOrder: CustomerPreOrder;
  bookingTypeName: string;
}

export interface PreorderContextValue {
  bookingData: Booking;
  customerPreOrder: CustomerPreOrder | undefined;
  customerPreOrders: Record<string, CustomerPreOrder>; // Map booking type to customer pre-order
  currency: string;
  countryCode: string;
  hidePricesByMenu: Record<string, boolean>;
  shouldHidePrices: boolean;
  forNames: string[];
  orderingFor: number;
  multipleNames: boolean;
  /**
   * Whether the guest may order for multiple people. False when the venue has
   * disabled the shareable pre-order link for this booking.
   */
  multiPersonOrderingEnabled: boolean;
  updateCustomerPreOrder: (request: CustomerPreOrderItemUpdateRequest) => Promise<boolean>;
  /** Whether an update for this item is in flight, so the menu and basket can lock its row together. */
  isItemPending: (itemId: string, menuId: string, personIndex: number) => boolean;
  linkedBookings: LinkedBooking[] | null;
  bookingTypeName?: string;
  bookingTypeId?: string;
}

const PreorderContext = createContext<PreorderContextValue | undefined>(undefined);

export function usePreorderContext(): PreorderContextValue {
  const context = useContext(PreorderContext);
  if (context === undefined) {
    throw new Error(
      "usePreorderContext must be used within a <PreorderProvider>. Wrap a parent component with <PreorderProvider>.",
    );
  }
  return context;
}

interface PreorderProviderProps {
  readonly value: PreorderContextValue;
  readonly children: React.ReactNode;
}

export function PreorderProvider({ value, children }: PreorderProviderProps) {
  return <PreorderContext.Provider value={value}>{children}</PreorderContext.Provider>;
}
